if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');
        this.idleText = this.submitButtonText ? this.submitButtonText.textContent.trim() : '';
        this.hideErrors = this.dataset.hideErrors === 'true';

        // Handle the tap on the button itself, so the browser never fires a form "submit"
        // (other scripts react to every form submit, which made the tap slow)
        this.submitButton.addEventListener('click', this.onSubmitHandler.bind(this));
        // Fallback: Enter key inside the form
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true' || this.submitButton.disabled) return;

        this.handleErrorMessage();

        const formData = new FormData(this.form);
        const qty = parseInt(formData.get('quantity'), 10) || 1;

        // Ignore an accidental double-tap (two taps within 0.6 s)
        const now = Date.now();
        if (now - (this.lastTap || 0) < 600) return;
        this.lastTap = now;

        // 1) In the tap: only the button text changes (one tiny update)
        this.showAdded();

        // 2) Right after the screen is drawn: cart count, toast and the real request
        this.afterPaint(() => {
          this.bumpCartCount(qty);
          ProductForm.showToast('Added to cart');
          this.sendAdd(formData, evt, qty);
        });
      }

      sendAdd(formData, evt, qty) {
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;
        config.keepalive = true; // finish the add even if the shopper leaves the page
        config.priority = 'high'; // ask the browser to send this before background requests

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.undoAdded();
              this.syncCartCount();
              ProductForm.showToast(response.description || 'Could not add', true);
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            this.error = false;
            // The theme-wide cart update runs when the page is idle, not right after a tap
            this.whenIdle(() => {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              });
            });
            this.syncCartCount();

            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) quickAddModal.hide(true);
          })
          .catch((e) => {
            console.error(e);
            this.undoAdded();
            ProductForm.showToast('Could not add. Please try again.', true);
            this.syncCartCount();
            this.handleErrorMessage(window.cartStrings && window.cartStrings.error);
          })
          .finally(() => {
            if (window.CartPerformance) CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      // Toast: compact, no animation, with a View cart button
      static showToast(message, isError) {
        let el = document.getElementById('arc-toast');
        if (!el) {
          const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
          el = document.createElement('div');
          el.id = 'arc-toast';
          el.setAttribute('role', 'status');
          el.setAttribute('aria-live', 'polite');
          el.style.cssText =
            'position:fixed;left:50%;transform:translateX(-50%);' +
            'bottom:calc(80px + env(safe-area-inset-bottom,0px));z-index:9999;' +
            'display:none;align-items:center;gap:12px;' +
            'border-radius:999px;background:#1c1c1c;color:#fff;' +
            'font-size:13px;font-weight:500;line-height:1;white-space:nowrap;max-width:92vw;' +
            'box-shadow:0 2px 10px rgba(0,0,0,.2);';
          el.innerHTML =
            '<span class="arc-toast__msg"></span>' +
            '<a class="arc-toast__link" href="' + cartUrl + '" ' +
            'style="padding:7px 12px;border-radius:999px;background:#fff;color:#7B014C;' +
            'font-size:12px;font-weight:700;text-decoration:none;line-height:1;">View cart</a>';
          document.body.appendChild(el);
        }

        const msg = el.querySelector('.arc-toast__msg');
        const link = el.querySelector('.arc-toast__link');
        msg.textContent = (isError ? '✕ ' : '✓ ') + message;
        el.style.background = isError ? '#b00020' : '#1c1c1c';
        link.style.display = isError ? 'none' : 'inline-block';
        el.style.padding = isError ? '10px 16px' : '5px 5px 5px 14px';
        el.style.display = 'flex';

        clearTimeout(ProductForm.toastTimer);
        ProductForm.toastTimer = setTimeout(() => {
          el.style.display = 'none';
        }, isError ? 2500 : 3500);
      }

      // Button shows progress while Shopify adds the item
      showAdding() {
        if (!this.submitButtonText) return;
        clearTimeout(this.addedTimer);
        this.submitButtonText.textContent = 'Adding…';
      }

      // Runs after the next screen update, at a priority above background tasks
      afterPaint(fn) {
        requestAnimationFrame(() => {
          if (window.scheduler && window.scheduler.postTask) {
            window.scheduler.postTask(fn, { priority: 'user-visible' });
          } else {
            setTimeout(fn, 0);
          }
        });
      }

      // Button confirms the add for 1.5 s
      showAdded() {
        if (!this.submitButtonText) return;
        this.submitButtonText.textContent = 'Added ✓';
        clearTimeout(this.addedTimer);
        this.addedTimer = setTimeout(() => this.undoAdded(), 1500);
      }

      undoAdded() {
        if (!this.submitButtonText) return;
        clearTimeout(this.addedTimer);
        this.submitButtonText.textContent = this.idleText || window.variantStrings.addToCart;
      }

      // Header cart count
      setCartCount(count) {
        const bubble = document.getElementById('cart-icon-bubble');
        if (!bubble) return;
        let badge = bubble.querySelector('.cart-count-bubble');
        if (count <= 0) {
          if (badge) badge.remove();
          return;
        }
        if (!badge) {
          badge = document.createElement('div');
          badge.className = 'cart-count-bubble';
          bubble.appendChild(badge);
        }
        badge.innerHTML =
          '<span aria-hidden="true">' + count + '</span>' +
          '<span class="visually-hidden">' + count + ' items</span>';
      }

      // Instant bump on tap
      bumpCartCount(qty) {
        const bubble = document.getElementById('cart-icon-bubble');
        const visible = bubble && bubble.querySelector('.cart-count-bubble span[aria-hidden="true"]');
        const current = visible ? parseInt(visible.textContent, 10) || 0 : 0;
        this.setCartCount(current + qty);
      }

      // Runs a function when the browser is idle
      whenIdle(fn) {
        if (window.requestIdleCallback) requestIdleCallback(fn, { timeout: 2000 });
        else setTimeout(fn, 300);
      }

      // Confirms the real number once Shopify has replied.
      // Waits until taps stop, so quick repeated adds make one check instead of many.
      syncCartCount() {
        clearTimeout(ProductForm.syncTimer);
        ProductForm.syncTimer = setTimeout(() => {
          fetch(`${routes.cart_url}.js`, { cache: 'no-store' })
            .then((r) => r.json())
            .then((cart) => this.setCartCount(cart.item_count))
            .catch(() => {});
        }, 600);
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}